import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, FileText, Search, BarChart3, Share2, Play, AlertTriangle } from "lucide-react";
import { ContentReviewPanel } from "./ContentReviewPanel";
import { useContentStore, CONTENT_STAGES, STAGE_COLOR_MAP, EMPTY_ITEMS, EMPTY_SOCIAL_POSTS, getItemMeta, type ContentItem } from "@/stores/contentStore";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface ContentItemDetailProps {
  itemId: string;
  projectId: number;
}

const PIPELINE_IDS = new Set(["researching", "outlining", "writing", "reviewing", "approved", "published"]);
const PIPELINE_STAGES = CONTENT_STAGES.filter((s) => PIPELINE_IDS.has(s.id));

const STARTABLE = new Set(["queued", "scheduled", "error", "killed"]);

// ── Overview Tab ──────────────────────────────────────────────────────

function OverviewTab({ item, projectId }: { item: ContentItem; projectId: number }) {
  const stageInfo = CONTENT_STAGES.find((s) => s.id === item.stage);
  const fields: [string, string | null | undefined][] = [
    ["Type", item.content_type],
    ["Primary Keyword", item.primary_keyword],
    ["Target Words", item.target_word_count?.toLocaleString()],
    ["Word Count", item.word_count > 0 ? item.word_count.toLocaleString() : null],
    ["Scheduled", item.scheduled_publish_date],
    ["Published", item.published_at],
  ];

  return (
    <div className="space-y-4">
      {(item.stage === "error" || item.stage === "killed") && item.last_error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-red-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Pipeline {item.stage === "killed" ? "killed" : "failed"}
          </div>
          <p className="text-xs text-red-300/80">{item.last_error}</p>
        </div>
      )}

      {STARTABLE.has(item.stage) && (
        <button
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          onClick={async () => {
            try {
              await useContentStore.getState().startPipeline(projectId, item.id);
            } catch {
              toast({ title: "Failed to start pipeline", variant: "destructive" });
            }
          }}
        >
          <Play className="h-4 w-4" /> {(item.stage === "error" || item.stage === "killed") ? "Restart" : "Start"} Content Pipeline
        </button>
      )}

      {PIPELINE_STAGES.some((s) => s.id === item.stage) && (() => {
        const currentIdx = PIPELINE_STAGES.findIndex((s) => s.id === item.stage);
        return (
          <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 p-3">
            <div className="mb-2 text-xs font-medium text-zinc-400">Pipeline Progress</div>
            <div className="flex items-center gap-1">
              {PIPELINE_STAGES.map((s, stepIdx) => {
                const isDone = stepIdx < currentIdx;
                const isCurrent = s.id === item.stage;
                return (
                  <div key={s.id} className="flex flex-1 flex-col items-center gap-1">
                    <div className={`h-1.5 w-full rounded-full ${isDone ? "bg-green-500" : isCurrent ? "bg-indigo-500" : "bg-zinc-700"}`} />
                    <span className={`text-[10px] ${isCurrent ? "font-medium text-indigo-400" : "text-zinc-500"}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: stageInfo?.color }}
        />
        <span className="text-sm font-medium">{stageInfo?.label}</span>
      </div>

      {item.published_url && (
        <div className="flex items-center gap-2 text-xs">
          <ExternalLink size={12} className="text-green-500 shrink-0" />
          <a
            href={item.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline truncate"
          >
            {item.published_url}
          </a>
        </div>
      )}

      {item.blog_idea && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Blog Idea</p>
          <p className="text-sm">{item.blog_idea}</p>
        </div>
      )}

      <div className="space-y-2">
        {fields.map(([label, value]) =>
          value ? (
            <div key={label} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span>{value}</span>
            </div>
          ) : null
        )}
      </div>

      {item.meta_title && (
        <div className="space-y-1 border-t border-border/40 pt-3">
          <div className="flex justify-between">
            <p className="text-xs text-muted-foreground">Meta Title</p>
            <span className="text-xs text-muted-foreground">{item.meta_title.length}/60</span>
          </div>
          <p className="text-sm">{item.meta_title}</p>
        </div>
      )}

      {item.meta_description && (
        <div className="space-y-1">
          <div className="flex justify-between">
            <p className="text-xs text-muted-foreground">Meta Description</p>
            <span className="text-xs text-muted-foreground">{item.meta_description.length}/160</span>
          </div>
          <p className="text-sm text-muted-foreground">{item.meta_description}</p>
        </div>
      )}
    </div>
  );
}

// ── Research Tab ──────────────────────────────────────────────────────

function ResearchTab({ item }: { item: ContentItem }) {
  const brief = item.research_brief;
  const keywords = item.secondary_keywords || [];

  return (
    <div className="space-y-4">
      {keywords.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Secondary Keywords</p>
          <div className="flex flex-wrap gap-1">
            {keywords.map((kw) => (
              <Badge key={kw} variant="secondary" className="text-xs">{kw}</Badge>
            ))}
          </div>
        </div>
      )}

      {brief ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Research Brief</p>
          <div className="text-sm whitespace-pre-wrap bg-muted/30 rounded-md p-3">
            {brief}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No research data yet.</p>
      )}
    </div>
  );
}

// ── Drafts Tab ───────────────────────────────────────────────────────

function DraftsTab({ item }: { item: ContentItem }) {
  const content = item.final_content || item.draft_content;

  if (!content) {
    return <p className="text-sm text-muted-foreground">No draft content yet.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">
          {item.final_content ? "Final Content" : "Draft"}
        </p>
        <span className="text-xs text-muted-foreground">
          {item.word_count.toLocaleString()} words
        </span>
      </div>
      <div className="text-sm whitespace-pre-wrap bg-muted/30 rounded-md p-3 max-h-[500px] overflow-y-auto">
        {content}
      </div>
    </div>
  );
}

// ── SEO Tab ──────────────────────────────────────────────────────────

function SeoTab({ item }: { item: ContentItem }) {
  const aiScore = item.ai_detection_score;
  const seoScore = getItemMeta<number>(item, "seo_score");

  const checks: { label: string; ok: boolean }[] = [
    { label: "Has meta title", ok: !!item.meta_title },
    { label: "Meta title ≤ 60 chars", ok: (item.meta_title?.length || 0) <= 60 },
    { label: "Has meta description", ok: !!item.meta_description },
    { label: "Meta description ≤ 160 chars", ok: (item.meta_description?.length || 0) <= 160 },
    { label: "Has primary keyword", ok: !!item.primary_keyword },
    { label: "Word count ≥ target", ok: !!(item.target_word_count && item.word_count >= item.target_word_count) },
  ];

  return (
    <div className="space-y-4">
      {(aiScore !== undefined || seoScore !== undefined) && (
        <div className="grid grid-cols-2 gap-3">
          {aiScore !== undefined && (
            <div className="rounded-md border border-border/40 p-3 text-center">
              <p className="text-2xl font-bold">{aiScore}%</p>
              <p className="text-xs text-muted-foreground">AI Detection</p>
            </div>
          )}
          {seoScore !== undefined && (
            <div className="rounded-md border border-border/40 p-3 text-center">
              <p className="text-2xl font-bold">{seoScore}</p>
              <p className="text-xs text-muted-foreground">SEO Score</p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">SEO Checklist</p>
        {checks.map((c) => (
          <div key={c.label} className="flex items-center gap-2 text-sm">
            <div className={cn(
              "w-3 h-3 rounded-full",
              c.ok ? "bg-green-500" : "bg-red-500/50"
            )} />
            <span className={c.ok ? "" : "text-muted-foreground"}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Distribution Tab ─────────────────────────────────────────────────

function DistributionTab({ item }: { item: ContentItem }) {
  const socialPosts = useContentStore((s) => s.socialPosts[item.id] ?? EMPTY_SOCIAL_POSTS);
  const fetchSocialPosts = useContentStore((s) => s.fetchSocialPosts);

  useEffect(() => {
    void fetchSocialPosts(item.id);
  }, [item.id, fetchSocialPosts]);

  return (
    <div className="space-y-4">
      {item.published_url && (
        <a
          href={item.published_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-blue-400 hover:underline"
        >
          <ExternalLink className="w-3 h-3" />
          {item.published_url}
        </a>
      )}

      <div className="space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">
          Social Posts ({socialPosts.length})
        </p>
        {socialPosts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No social posts yet.</p>
        ) : (
          socialPosts.map((post) => (
            <div key={post.id} className="rounded-md border border-border/40 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">{post.platform}</Badge>
                <Badge variant="outline" className="text-xs">{post.status}</Badge>
              </div>
              <p className="text-sm line-clamp-3">{post.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export function ContentItemDetail({ itemId, projectId }: ContentItemDetailProps) {
  const selectItem = useContentStore((s) => s.selectItem);
  const cachedItem = useContentStore((s) =>
    (s.items[projectId] ?? EMPTY_ITEMS).find((i) => i.id === itemId)
  );
  const [item, setItem] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (cachedItem) {
      setItem(cachedItem);
      setLoading(false);
    }
    api.get<ContentItem>(`/api/content-items/${itemId}`)
      .then((data) => {
        if (!cancelled) { setItem(data); setLoading(false); }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sheet open onOpenChange={(open) => { if (!open) selectItem(null); }}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="pr-8">
            {loading ? <Skeleton className="h-6 w-48" /> : (item?.title || "Untitled")}
          </SheetTitle>
          <SheetDescription>
            {item?.content_type && (
              <Badge variant="secondary" className="text-xs">{item.content_type}</Badge>
            )}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : item ? (
          <div className="mt-4">
            {item.stage === "reviewing" && (
              <div className="mb-4">
                <ContentReviewPanel item={item} projectId={projectId} />
              </div>
            )}

            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full grid grid-cols-5">
                <TabsTrigger value="overview" className="text-xs">
                  <FileText className="w-3 h-3 mr-1" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="research" className="text-xs">
                  <Search className="w-3 h-3 mr-1" />
                  Research
                </TabsTrigger>
                <TabsTrigger value="drafts" className="text-xs">
                  <FileText className="w-3 h-3 mr-1" />
                  Drafts
                </TabsTrigger>
                <TabsTrigger value="seo" className="text-xs">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  SEO
                </TabsTrigger>
                <TabsTrigger value="distribution" className="text-xs">
                  <Share2 className="w-3 h-3 mr-1" />
                  Distrib.
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <OverviewTab item={item} projectId={projectId} />
              </TabsContent>
              <TabsContent value="research" className="mt-4">
                <ResearchTab item={item} />
              </TabsContent>
              <TabsContent value="drafts" className="mt-4">
                <DraftsTab item={item} />
              </TabsContent>
              <TabsContent value="seo" className="mt-4">
                <SeoTab item={item} />
              </TabsContent>
              <TabsContent value="distribution" className="mt-4">
                <DistributionTab item={item} />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-4">Item not found.</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
